function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

class Accordion extends HTMLElement {
  constructor(header, body, isOpen, family, event, closeOnResized, initialized) {
    super();
  }

  connectedCallback() {
    this.init();
  }

  init() {
    if (this.closest('mca-dialog')) {
      this.classList.add('initialized');
      return;
    }

    // check if header and body exists
    this.header = this.querySelector('mca-accordion-header');
    if (!this.header) {
      throw new Error('Accordion must have a header element');
    }
    this.body = this.querySelector('mca-accordion-body');
    if (!this.body) {
      throw new Error('Accordion must have a body element');
    }

    // get initial state
    this.isOpen = this.hasAttribute('open');
    this.family = this.getAttribute('family') ?? null;
    this.event = this.getAttribute('event') ?? 'click';
    this.closeOnResized = !(this.getAttribute('close-on-resized') === 'false');
    this.clickOutsideClose =
      this.getAttribute('click-outside-close') ??
      (this.getAttribute('click-outside-close') === 'true' || this.getAttribute('click-outside-close') === '')
        ? true
        : this.clickOutsideClose;

    this.setStyle();
    this.setInitialAttributes();

    if (this.isOpen) {
      this.open(true);
    } else {
      this.close();
    }

    this.watchEvents();
    this.classList.add('initialized');
    this.initialized = true;
    this.dispatchEvent(new CustomEvent('accordion:initialized', { bubbles: true }));
  }

  setStyle() {
    this.style.display = 'block';
    if (!this.body || !this.header) return;
    this.header.style.cursor = 'pointer';
    this.body.style.overflow = 'hidden';
    this.body.style.display = 'block';
    this.body.style.maxHeight = this.isOpen ? this.body.scrollHeight + 'px' : '0';
    this.body.style.transition = 'max-height .4s cubic-bezier(.33,0,.23,1)';
  }

  setInitialAttributes() {
    this.setAttribute('aria-expanded', this.isOpen ? 'true' : 'false');
    if (this.header) {
      const header = this.header;
      if (header.querySelectorAll('a, button').length === 0) {
        header.setAttribute('tabindex', '0');
        this.watchKeyboard(header);
      }
    }
  }

  watchKeyboard(target) {
    const keydownEvent = (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        this.toggle();

        target.blur();
        target.removeEventListener('keydown', keydownEvent, false);
        sleep(300).then(() => {
          this.watchKeyboard(target);
        });
      }
    };
    target.addEventListener('keydown', keydownEvent, false);
  }

  watchEvents() {
    this.addEventListener(
      this.event,
      (e) => {
        e.stopPropagation();
        const target = e.target;
        if (!target || target.closest('mca-accordion') != this) return;
        if (this.header.contains(target) && target.closest('[stop-propagation]')) return;
        switch (this.event) {
          case 'mouseover':
            this.open();

            this.addEventListener('mouseleave', () => {
              this.close();
            });
            break;
          default:
            target.closest('mca-accordion-header') && this.toggle();
            this.header && (this.header.style.pointerEvents = 'none');
            sleep(300).then(() => {
              this.header && (this.header.style.pointerEvents = 'auto');
            });
            break;
        }
      },
      false
    );

    if (this.closeOnResized) {
      window.addEventListener('resize', () => {
        this.setHeight();
      });
    }
  }

  setHeight = () => {
    if (this.body) {
      const body = this.body;
      body.style.maxHeight = body.scrollHeight + 'px';
      if (this.isOpen) {
        body.addEventListener('transitionend', () => {
          if (this.isOpen) body.style.maxHeight = 'none';
        });
      } else {
        sleep(0.1).then(() => {
          body.style.maxHeight = '0';
        });
      }
    }
  };

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open(firstTime = false) {
    if (!firstTime) this.closeAccordionFamily();
    this.classList.add('active');
    this.setAttribute('aria-expanded', '');
    this.setAttribute('open', '');
    this.isOpen = true;
    this.setHeight();
    if (this.clickOutsideClose) {
      this.watchClickOutside();
    }
    this.dispatchEvent(new CustomEvent('accordion:open', { bubbles: true }));
  }

  close() {
    if (this.isOpen === false) return;
    if (this.body) {
      const body = this.body;
      body.style.overflow = 'hidden';
      if (body.querySelectorAll('a, button').length > 0) {
        body.querySelectorAll('a, button').forEach((action) => {
          action.setAttribute('tabindex', '-1');
        });
      }
    }
    this.classList.contains('active') && this.classList.remove('active');
    this.hasAttribute('aria-expanded') && this.removeAttribute('aria-expanded');
    this.hasAttribute('open') && this.removeAttribute('open');
    this.isOpen = false;
    this.setHeight();
    this.closeAccordionChildren();
    this.dispatchEvent(new CustomEvent('accordion:close', { bubbles: true }));
  }

  closeAccordionChildren = () => {
    const accordionsChildren = this.querySelectorAll('mca-accordion');

    accordionsChildren.forEach((accordion) => {
      if (accordion.isOpen) {
        accordion.close();
      }
    });
  };

  closeAccordionFamily = () => {
    if (!this.family) return;
    const accordionsFamilies = document.querySelectorAll('mca-accordion[family]');

    accordionsFamilies.forEach((accordion) => {
      const family = accordion.getAttribute('family');
      if (this.family === family && accordion.isOpen) {
        accordion.close();
      }
    });
  };

  watchClickOutside() {
    const clickOutsideEvent = (e) => {
      if (e.target.closest('mca-accordion') != this) {
        this.close();
        document.removeEventListener('click', clickOutsideEvent, false);
      }
    };
    document.addEventListener('click', clickOutsideEvent, false);
  }
}

class AccordionHeader extends HTMLElement {
  constructor() {
    super();
  }
}

class AccordionBody extends HTMLElement {
  constructor() {
    super();
  }
}

if (!customElements.get('mca-accordion')) {
  customElements.define('mca-accordion', Accordion);
}
